import { useQuery } from "@tanstack/react-query";

import { buildRentalUpdateRequestBody } from "features/subscriptions/helpers/rentalSubscriptions";
import type { SubscriptionEstimate } from "features/subscriptions/types";
import type { getCouponUpdateState } from "features/subscriptions/utils";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

type CouponUpdateState = ReturnType<typeof getCouponUpdateState>;

async function rentalEstimateUpdateRequest(
  workspaceId: string,
  subscriptionId: string,
  {
    currentCount,
    newCount,
    currentItemPriceId,
    newItemPriceId,
    couponUpdateState,
  }: {
    currentCount: number;
    newCount: number;
    currentItemPriceId: string;
    newItemPriceId: string | undefined;
    couponUpdateState?: CouponUpdateState;
  },
): Promise<SubscriptionEstimate> {
  const body = buildRentalUpdateRequestBody({
    currentCount,
    newCount,
    currentItemPriceId,
    newItemPriceId,
    couponUpdateState,
    isEstimate: true,
  });

  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/rental/estimate`,
    body,
  );
  return estimate;
}

export default function useRentalEstimateUpdate({
  subscriptionId,
  currentCount,
  newCount,
  currentItemPriceId,
  newItemPriceId,
  couponUpdateState,
  isEnabled = true,
}: {
  subscriptionId: string;
  currentCount: number;
  newCount: number;
  currentItemPriceId: string;
  newItemPriceId: string | undefined;
  couponUpdateState?: CouponUpdateState;
  isEnabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const {
    data: estimateUpdate,
    isFetching: isLoadingEstimateUpdate,
    error: estimateUpdateError,
  } = useQuery({
    queryKey: getQueryKeys(workspaceId).subscriptionKeys.rentalUpdateEstimate(
      subscriptionId,
      newCount,
      newItemPriceId,
      couponUpdateState,
    ),
    queryFn: () =>
      rentalEstimateUpdateRequest(workspaceId, subscriptionId, {
        currentCount,
        newCount,
        currentItemPriceId,
        newItemPriceId,
        couponUpdateState,
      }),
    enabled: isEnabled,
    gcTime: 0,
  });

  return { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError };
}
